import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { useProductsList } from "./use-products-list";
import { CatalogStatusBadge } from "./CatalogStatusBadge";
import { CreateProductDialog } from "./CreateProductDialog";
import { UpdateProductDialog } from "./UpdateProductDialog";
import { ProductMetadataSummary } from "./ProductMetadataSummary";
import type { StripeProduct } from "./types";

interface ProductsSectionProps {
  readonly canEdit: boolean;
}

export function ProductsSection({ canEdit }: ProductsSectionProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useProductsList();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<StripeProduct | null>(null);

  return (
    <section data-testid="catalog-products-section" aria-labelledby="catalog-products-heading">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 id="catalog-products-heading" className="font-display text-xl">
          {t("catalog.products.title")}
        </h2>
        {canEdit && (
          <Button data-testid="create-product-button" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("catalog.products.actions.create")}
          </Button>
        )}
      </div>

      {isLoading && (
        <p
          data-testid="catalog-products-loading"
          className="font-sans text-sm text-muted-foreground"
        >
          {t("catalog.loading")}
        </p>
      )}

      {isError && (
        <div
          data-testid="catalog-products-error"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">
            {t("catalog.products.error")}
          </p>
        </div>
      )}

      {data && data.length === 0 && (
        <div
          data-testid="catalog-products-empty"
          className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center"
        >
          <p className="font-sans text-sm font-medium text-foreground">
            {t("catalog.products.emptyTitle")}
          </p>
          <p className="mt-1 font-sans text-sm font-normal text-muted-foreground">
            {t("catalog.products.emptyBody")}
          </p>
        </div>
      )}

      {data && data.length > 0 && (
        <div data-testid="catalog-products-list" className="space-y-3">
          {data.map((product) => (
            <article
              key={product.id}
              data-testid={`product-row-${product.id}`}
              className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-border bg-card p-4"
            >
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-sans text-base font-medium text-foreground">
                    {product.name}
                  </h3>
                  <CatalogStatusBadge
                    active={product.active}
                    testId={`product-status-${product.id}`}
                  />
                </div>
                <p className="font-mono text-xs text-muted-foreground">{product.id}</p>
                {product.description && (
                  <p className="font-sans text-sm text-muted-foreground">{product.description}</p>
                )}
                <ProductMetadataSummary product={product} />
              </div>
              {canEdit && (
                <Button
                  data-testid={`edit-product-${product.id}`}
                  variant="outline"
                  size="sm"
                  onClick={() => setEditTarget(product)}
                >
                  {t("catalog.products.actions.edit")}
                </Button>
              )}
            </article>
          ))}
        </div>
      )}

      <CreateProductDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={() => setCreateOpen(false)}
      />
      <UpdateProductDialog
        product={editTarget}
        open={editTarget !== null}
        onOpenChange={(open) => {
          if (!open) setEditTarget(null);
        }}
        onSuccess={() => setEditTarget(null)}
      />
    </section>
  );
}
